import type { LogicSubCondition } from '@webapp/common/entities/logic';
import { LogicSubConditonType } from '@webapp/common/entities/logic';
import { isAllPagesOnOnePage } from '@webapp/common/lib/const';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { QuestionLogicModel, SurveyStore } from '@webapp/common/resources/entities';
import { BundleType } from '@webapp/common/resources/entities';
import { isOrderVisible } from '@webapp/common/resources/question-types';
import {
    checkIsQWithClickReply,
    LinkParamsLogicType,
    LogicAction,
    LogicBoolFunc,
    LogicTransition,
    LogicTransitionType,
    LogicTransitionVariant,
    LogicType,
    QuestionType,
} from '@webapp/common/resources/survey';

import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';

import type { QuestionAnswerContext } from '../../entities/answer-machine';
import { BundleEntity } from '../../entities/bundle';
import type { GroupContext } from '../group-machine';
import { getQ } from '../lib';

import { createLastBundle, getGroupsValues } from './logic';

const isLinkParamLogic = (l: { type: LogicType }) => l.type === LogicType.LINK_PARAM;
const isCommonLogic = (l: { type: LogicType; isExtended?: boolean }) =>
    !l.isExtended &&
    (l.type === LogicType.ALWAYS ||
        l.type === LogicType.NOT_SELECT_VARIANTS ||
        l.type === LogicType.SELECT_VARIANTS ||
        l.type === LogicType.SELECT_ONLY_VARIANTS ||
        l.type === LogicType.SELECT_COUNT ||
        l.type === LogicType.CANT_RATE ||
        l.type === LogicType.SKIP);
const isExtendedLogic = (l: { isExtended?: boolean }) => l.isExtended;
const isAnswersHideActionLogic = (l: { action: string }) => l.action === LogicAction.ANSWERS_HIDE;
const isNotAnswersHideActionLogic = (l: { action: string }) => l.action !== LogicAction.ANSWERS_HIDE;
const isLogicWithFinishType = (l: {
    transition: LogicTransition;
    finishTransition?: LogicTransition;
    transitionType: LogicTransitionType;
}) => {
    if (l.transition === LogicTransition.FINISH_SURVEY && l.finishTransition !== LogicTransition.REGULAR_COMPLETE) {
        return true;
    }
    if (
        l.transition === LogicTransition.DISQUAL ||
        l.transition === LogicTransition.PAGES ||
        l.transition === LogicTransition.PERSONAL_COMPLETE ||
        l.transition === LogicTransition.QUESTIONS
    ) {
        return l.transitionType !== LogicTransitionType.STANDART;
    }
    return false;
};

const isLogicWithOnlyFinishType = (l: { transition: LogicTransition }) => {
    if (
        l.transition === LogicTransition.FINISH_SURVEY ||
        l.transition === LogicTransition.DISQUAL ||
        l.transition === LogicTransition.PERSONAL_COMPLETE ||
        l.transition === LogicTransition.REGULAR_COMPLETE
    ) {
        return true;
    }
    return false;
};

const filterLogicsByFinishTypeOrder = (logics: Array<LogicWithTypeAndQuestionId>, bundles: Array<BundleEntity>) => {
    return logics
        .filter(isNotAnswersHideActionLogic)
        .filter(isLogicWithFinishType)
        .sort((a, b) => {
            const { bundleIdx: aBundleIndex, questionIdx: aQuestionIndex } = getQBundleIdxAndQuestionIdx(
                a.qId,
                bundles,
            );
            const { bundleIdx: bBundleIndex, questionIdx: bQuestionIndex } = getQBundleIdxAndQuestionIdx(
                b.qId,
                bundles,
            );
            if (aBundleIndex === bBundleIndex) {
                return bQuestionIndex - aQuestionIndex;
            }
            return bBundleIndex - aBundleIndex;
        });
};
const getMatchedLinkParams = (queryParamsRaw: Record<string, string>, linkParamsRaw: Record<string, string>) => {
    const queryParams = queryParamsRaw || {};
    const linkParams = linkParamsRaw || {};
    const reversedQpKey = Object.keys(linkParams).reduce((acc, item) => {
        acc[linkParams[item]] = item;
        return acc;
    }, {} as Record<string, string>);

    return Object.keys(queryParams).reduce((acc, qpKey) => {
        if (reversedQpKey[qpKey]) {
            acc[reversedQpKey[qpKey]] = queryParams[qpKey];
        }
        return acc;
    }, {} as Record<string, string>);
};

const getMatchedLinkParamsLogics = (
    linkParamsLogics: Array<QuestionLogicModel>,
    linkParams: Record<string, string>,
): Array<LogicWithTypeAndQuestionId> => {
    return linkParamsLogics
        .filter((logic) =>
            logic.linkParamsLogicType === LinkParamsLogicType.NOT_EQUAL
                ? linkParams[logic.linkId]?.toLowerCase() !== logic.linkValue?.toLowerCase()
                : linkParams[logic.linkId]?.toLowerCase() === logic.linkValue?.toLowerCase(),
        )
        .map((l) => ({ ...l, logicType: 'link-param', qId: -1 }));
};

// Выбран ли в вопросе хотя бы один ответ, попадающий под квотную логику.
// Не зависит от состояния самой квоты (открыта/закрыта) — только от ответа
// респондента. Используется и логикой бандлов, и клиентом квот при сборе
// claim-запроса на сервис квот.
export const isQuotaLogicMatched = (
    logic: Pick<QuestionLogicModel, 'boolFunc' | 'params' | 'type' | 'id'>,
    question: QuestionActorRef,
): boolean => {
    const { answersActors, groupsActors, responseActor, type: questionType } = getQ(question);
    const groups = groupsActors.map((g) => g.getSnapshot().context);
    const answers = answersActors.map((a) => a.getSnapshot().context);
    const response = responseActor.getSnapshot().context;
    const { params } = logic;
    const notScale = questionType !== QuestionType.SCALE; // TODO fix backend, dont use groups
    const withGroups = Boolean(notScale && groups && groups.length > 0);
    const withAnswers = Boolean(notScale && answers && answers.length > 0);
    const variantsIds: Record<number, boolean> = params.reduce((acc, { id }) => {
        acc[id] = true;
        return acc;
    }, {}); // TODO use Set
    const answersById: Record<string, QuestionAnswerContext> = answers.reduce((acc, a) => {
        acc[a.id] = a;
        return acc;
    }, {});

    let isSomeOfAnswersSelected = false;
    if (questionType === QuestionType.MATRIX_RATING) {
        isSomeOfAnswersSelected = Object.keys(variantsIds).some((id) => {
            const [, rating, answerId] = id.toString().split('-');
            return answersById[answerId]?.response.getSnapshot().context.value?.toString() === rating;
        });
    } else if (questionType === QuestionType.MATRIX_SINGLE_ANSWER || questionType === QuestionType.MATRIX_FEW_ANSWERS) {
        isSomeOfAnswersSelected = Object.keys(variantsIds).some((id) => {
            const [, groupId, answerId] = id.toString().split('-');
            return !!groups.find((gr) => gr.id.toString() === groupId)?.responses[answerId]?.getSnapshot().context
                .value;
        });
    } else if (withGroups) {
        const values = getGroupsValues(groups);
        isSomeOfAnswersSelected = values.some((answerId) => variantsIds[answerId]);
    } else if (withAnswers) {
        isSomeOfAnswersSelected = answers.some(
            ({ id, response }) => !!response.getSnapshot().context.value && variantsIds[id],
        );
    } else {
        isSomeOfAnswersSelected = !!variantsIds[response.value as number];
    }
    return isSomeOfAnswersSelected;
};

const logicMatchQuota = (
    logic: Pick<QuestionLogicModel, 'boolFunc' | 'params' | 'type' | 'id'>,
    question: QuestionActorRef,
    quotaByQuestionId: Record<number, boolean>,
): boolean => quotaByQuestionId[logic.id] === false && isQuotaLogicMatched(logic, question);

const isSelectedValue = (value: unknown): boolean => Boolean(value) && value !== CANTRATE_ID;

const isCantRateRowSelected = (groups: Array<GroupContext>, answerId: string): boolean =>
    groups.some((gr) => gr.responses?.[answerId]?.getSnapshot()?.context?.value === CANTRATE_ID);

const logicMatchCommonOrExtended = (
    logic: Pick<QuestionLogicModel, 'boolFunc' | 'params' | 'type' | 'id' | 'minCount' | 'maxCount'> & {
        action?: string;
    },
    question: QuestionActorRef,
): boolean => {
    if (isAllPagesOnOnePage && !question.getSnapshot().context.dirty) return false;
    const { answersActors, groupsActors, responseActor, type: questionType, params: questionParams } = getQ(question);
    const groups = groupsActors.map((g) => g.getSnapshot().context);
    const answers = answersActors
        .map((a) => a.getSnapshot().context)
        .filter((answer) => !answer.hidden && !answer.hiddenByAnswersHide);
    const response = responseActor.getSnapshot().context;
    const { boolFunc, params, type, minCount, maxCount } = logic;
    const notScale = questionType !== QuestionType.SCALE; // TODO fix backend, dont use groups
    const withGroups = Boolean(notScale && groups && groups.length > 0);
    const withAnswers = Boolean(notScale && answers && answers.length > 0);
    const variantsIds: Record<number, boolean> = params.reduce((acc, { id }) => {
        acc[id] = true;
        return acc;
    }, {}); // TODO use Set
    const answersById: Record<string, QuestionAnswerContext> = answers.reduce((acc, a) => {
        acc[a.id] = a;
        return acc;
    }, {});
    let satisfied = false;

    switch (type) {
        case LogicType.SELECT_VARIANTS:
            switch (boolFunc) {
                case LogicBoolFunc.AND:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; rating: string }> } = Object.keys(
                            variantsIds,
                        ).reduce((acc, id) => {
                            const [, rating, rowId] = id.toString().split('-');
                            if (!acc[rowId]) {
                                acc[rowId] = [{ rowId, rating }];
                            } else {
                                acc[rowId].push({ rowId, rating });
                            }
                            return acc;
                        }, {} as { [key: string]: Array<{ rowId: string; rating: string }> });
                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];
                                return rowAnswers.some(
                                    (rowAns) =>
                                        answersById[rowId]?.response?.getSnapshot().context.value?.toString() ===
                                        rowAns.rating,
                                );
                            });
                    } else if (
                        questionType === QuestionType.MATRIX_SINGLE_ANSWER ||
                        questionType === QuestionType.MATRIX_FEW_ANSWERS ||
                        questionType === QuestionType.MATRIX_TEXT_ANSWER ||
                        questionType === QuestionType.MATRIX_DROPDOWN_LIST
                    ) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; groupId: string }> } =
                            Object.keys(variantsIds).reduce((acc, id) => {
                                const [, groupId, rowId] = id.toString().split('-');
                                if (!acc[rowId]) {
                                    acc[rowId] = [{ rowId, groupId }];
                                } else {
                                    acc[rowId].push({ rowId, groupId });
                                }
                                return acc;
                            }, {} as { [key: string]: Array<{ rowId: string; groupId: string }> });
                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];
                                return rowAnswers.some((rowAns) =>
                                    rowAns.groupId === String(CANTRATE_ID)
                                        ? isCantRateRowSelected(groups, rowAns.rowId)
                                        : !!groups
                                              .find((gr) => gr.id.toString() === rowAns.groupId)
                                              ?.responses[rowAns.rowId]?.getSnapshot().context.value,
                                );
                            });
                    } else if (withGroups) {
                        // const values = getGroupsValues(groups);
                        // satisfied = values.some((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        satisfied =
                            Object.keys(variantsIds).length > 0 &&
                            Object.keys(variantsIds).every((id) =>
                                Number(id) === CANTRATE_ID
                                    ? response.value === CANTRATE_ID
                                    : !!answersById[id]?.response.getSnapshot().context.value,
                            );
                    }
                    break;
                case LogicBoolFunc.OR:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        satisfied = Object.keys(variantsIds).some((id) => {
                            const [, rating, answerId] = id.toString().split('-');
                            return answersById[answerId]?.response.getSnapshot().context.value?.toString() === rating;
                        });
                    } else if (
                        questionType === QuestionType.MATRIX_SINGLE_ANSWER ||
                        questionType === QuestionType.MATRIX_FEW_ANSWERS ||
                        questionType === QuestionType.MATRIX_TEXT_ANSWER ||
                        questionType === QuestionType.MATRIX_DROPDOWN_LIST
                    ) {
                        satisfied = Object.keys(variantsIds).some((id) => {
                            const [, groupId, answerId] = id.toString().split('-');
                            if (groupId === String(CANTRATE_ID)) {
                                return isCantRateRowSelected(groups, answerId);
                            }
                            return !!groups
                                .find((gr) => gr.id.toString() === groupId)
                                ?.responses[answerId]?.getSnapshot().context.value;
                        });
                    } else if (withGroups) {
                        const values = getGroupsValues(groups);
                        satisfied = values.some((answerId) => variantsIds[answerId]);
                    } else if (withAnswers) {
                        satisfied =
                            answers.some(
                                ({ id, response }) => !!response.getSnapshot().context.value && variantsIds[id],
                            ) ||
                            (Boolean(variantsIds[CANTRATE_ID]) && response.value === CANTRATE_ID);
                    } else {
                        satisfied = !!variantsIds[response.value as number];
                    }
                    break;
            }

            break;
        case LogicType.NOT_SELECT_VARIANTS:
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            switch (boolFunc) {
                case LogicBoolFunc.AND:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; rating: string }> } = Object.keys(
                            variantsIds,
                        ).reduce((acc, id) => {
                            const [, rating, rowId] = id.toString().split('-');
                            if (!acc[rowId]) {
                                acc[rowId] = [{ rowId, rating }];
                            } else {
                                acc[rowId].push({ rowId, rating });
                            }
                            return acc;
                        }, {} as { [key: string]: Array<{ rowId: string; rating: string }> });

                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];

                                return rowAnswers.every(
                                    (rowAns) =>
                                        answersById[rowId]?.response?.getSnapshot().context.value?.toString() !==
                                        rowAns.rating,
                                );
                            });
                    } else if (withGroups) {
                        satisfied = Object.keys(variantsIds).every((variantId) => {
                            const [, groupId, answerId] = variantId.toString().split('-');
                            // псевдо-столбец «Не могу оценить»
                            if (groupId === String(CANTRATE_ID)) {
                                return !isCantRateRowSelected(groups, answerId);
                            }
                            const group = groups.find((group) => group.id.toString() === groupId);
                            if (!group) return true;
                            const value = group.responses?.[answerId]?.getSnapshot()?.context?.value;
                            return (
                                !value ||
                                // хитрый костыль для не могу оценить
                                value === CANTRATE_ID
                            );
                        });
                    } else if (withAnswers) {
                        satisfied =
                            Object.keys(variantsIds).length > 0 &&
                            Object.keys(variantsIds).every((id) =>
                                Number(id) === CANTRATE_ID
                                    ? response.value !== CANTRATE_ID
                                    : !answersById[id]?.response?.getSnapshot().context.value,
                            );
                    } else {
                    }

                    break;
                case LogicBoolFunc.OR:
                    if (questionType === QuestionType.MATRIX_RATING) {
                        const groupedByRowId: { [key: string]: Array<{ rowId: string; rating: string }> } = Object.keys(
                            variantsIds,
                        ).reduce((acc, id) => {
                            const [, rating, rowId] = id.toString().split('-');
                            if (!acc[rowId]) {
                                acc[rowId] = [{ rowId, rating }];
                            } else {
                                acc[rowId].push({ rowId, rating });
                            }
                            return acc;
                        }, {} as { [key: string]: Array<{ rowId: string; rating: string }> });

                        satisfied =
                            Object.keys(groupedByRowId).length > 0 &&
                            Object.keys(groupedByRowId).every((rowId) => {
                                const rowAnswers = groupedByRowId[rowId];

                                return rowAnswers.every(
                                    (rowAns) =>
                                        answersById[rowId]?.response?.getSnapshot().context.value?.toString() !==
                                        rowAns.rating,
                                );
                            });
                    } else if (withGroups) {
                        // responsesValues в контексте группы обновляется с лагом на один шаг
                        // (on.RESPONSE_CHANGE в group-machine), поэтому читаем response-акторы напрямую
                        const answered: Record<string, boolean> = {};
                        groups.forEach((gr) => {
                            Object.keys(gr.responses ?? {}).forEach((answerId) => {
                                const value = gr.responses[answerId]?.getSnapshot()?.context?.value;
                                if (value) {
                                    answered[`${question.getSnapshot().context.id}-${gr.id}-${answerId}`] = true;
                                }
                            });
                        });
                        satisfied = !Object.keys(variantsIds).every((id) => {
                            const [, groupId, answerId] = id.toString().split('-');
                            // псевдо-столбец «Не могу оценить»
                            if (groupId === String(CANTRATE_ID)) {
                                return isCantRateRowSelected(groups, answerId);
                            }
                            return answered[id];
                        });
                    } else if (withAnswers) {
                        if (checkIsQWithClickReply({ type: questionType, params: questionParams, clicks: [] })) {
                            const filtered = answers.filter(({ id }) => variantsIds[id]);
                            satisfied =
                                filtered.length > 0 &&
                                filtered.every(({ response }) => !response.getSnapshot().context.value);
                        } else {
                            // специально не answers, так как если answers, то для подстановки не работает...
                            satisfied = answersActors
                                .map((a) => a.getSnapshot().context)
                                .some(({ id, response }) => variantsIds[id] && !response.getSnapshot().context.value);
                        }
                        // псевдо-вариант «Не могу оценить»
                        if (variantsIds[CANTRATE_ID] && response.value !== CANTRATE_ID) {
                            satisfied = true;
                        }
                    } else {
                        satisfied = !variantsIds[response.value as number];
                    }

                    break;
            }

            break;
        case LogicType.SKIP:
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            if (!response.value) {
                satisfied = true;
            }

            if (withAnswers) {
                satisfied =
                    answers.length > 0 && answers.every(({ response }) => !response.getSnapshot().context.value);
            }

            if (withGroups) {
                satisfied =
                    groups.length > 0 &&
                    groups.every(({ responses }) => {
                        let empty = true;

                        for (const key of Object.keys(responses)) {
                            const response = responses[key].getSnapshot().context;
                            if (response.value) {
                                empty = false;
                                break;
                            }
                        }

                        return empty;
                    });
            }

            break;
        case LogicType.SELECT_ONLY_VARIANTS:
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            if (questionType === QuestionType.MATRIX_FEW_ANSWERS) {
                const selectedKeys: Array<string> = groups.flatMap((gr) =>
                    Object.keys(gr.responses ?? {})
                        .filter((answerId) => isSelectedValue(gr.responses[answerId]?.getSnapshot()?.context?.value))
                        .map((answerId) => `${gr.id}-${answerId}`),
                );
                satisfied =
                    selectedKeys.length > 0 &&
                    selectedKeys.every((key) =>
                        Object.keys(variantsIds).some((id) => id.toString().endsWith(`-${key}`)),
                    );
            } else if (withAnswers) {
                const selectedAnswers = answers.filter(({ response }) =>
                    isSelectedValue(response.getSnapshot().context.value),
                );
                satisfied = selectedAnswers.length > 0 && selectedAnswers.every(({ id }) => Boolean(variantsIds[id]));
            }
            break;
        case LogicType.SELECT_COUNT: {
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            let selectedCount = 0;
            if (questionType === QuestionType.MATRIX_FEW_ANSWERS) {
                selectedCount = groups.reduce(
                    (acc, gr) =>
                        acc +
                        Object.keys(gr.responses ?? {}).filter((answerId) =>
                            isSelectedValue(gr.responses[answerId]?.getSnapshot()?.context?.value),
                        ).length,
                    0,
                );
            } else if (withAnswers) {
                selectedCount = answers.filter(({ response }) =>
                    isSelectedValue(response.getSnapshot().context.value),
                ).length;
            }
            const min = typeof minCount === 'number' ? minCount : 1;
            satisfied = selectedCount >= min && (typeof maxCount !== 'number' || selectedCount <= maxCount);
            break;
        }
        case LogicType.CANT_RATE:
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            satisfied = response.value === CANTRATE_ID;
            if (!satisfied && withAnswers) {
                satisfied = answers.some(({ response }) => response.getSnapshot().context.value === CANTRATE_ID);
            }
            if (!satisfied && groups.length > 0) {
                satisfied = groups.some(({ responses }) =>
                    Object.keys(responses ?? {}).some(
                        (answerId) => responses[answerId]?.getSnapshot()?.context?.value === CANTRATE_ID,
                    ),
                );
            }
            break;
        case LogicType.ALWAYS:
            if (!question.getSnapshot().context.showByLogic) {
                break;
            }
            satisfied = true;

            break;
    }

    return satisfied;
};

const getMatchedQuotaLogics = (
    bundles: Array<BundleEntity>,
    quotaByQuestionId: Record<number, boolean>,
): Array<LogicWithTypeAndQuestionId> => {
    const matchedQuotaLogics: Array<LogicWithTypeAndQuestionId> = [];
    bundles.forEach((bundle) => {
        bundle.questions.forEach((question) => {
            question.getSnapshot().context.logics.forEach((logic) => {
                if (logic.type === LogicType.TYPE_QUESTION_QUOTA) {
                    if (logicMatchQuota(logic, question, quotaByQuestionId)) {
                        matchedQuotaLogics.push({
                            ...logic,
                            logicType: 'quota',
                            qId: question.getSnapshot().context.id,
                        });
                    }
                }
            });
        });
    });

    return matchedQuotaLogics;
};

const getQBundleIdxAndQuestionIdx = (qId: number, bundles: Array<BundleEntity>) => {
    const bundleIdx = bundles.findIndex((bundle) =>
        bundle.questions.find((question) => question.getSnapshot().context.id === qId),
    );
    const questionIdx =
        bundles[bundleIdx]?.questions.findIndex((question) => question.getSnapshot().context.id === qId) ?? -1;
    return { bundleIdx, questionIdx, qId };
};
const getQIdWithMaxIndex = (qIds: Array<number>, bundles: Array<BundleEntity>) => {
    const qBundleIdxAndQuestionIdx = qIds.map((qId) => getQBundleIdxAndQuestionIdx(qId, bundles));
    return (
        qBundleIdxAndQuestionIdx.sort((a, b) => {
            if (a.bundleIdx > b.bundleIdx) {
                return -1;
            }
            if (b.bundleIdx > a.bundleIdx) {
                return 1;
            }
            return b.questionIdx - a.questionIdx;
        })?.[0]?.qId ?? -1
    );
};

const getMatchedExtendedLogics = (
    bundles: Array<BundleEntity>,
    logics: Array<QuestionLogicModel>,
    linkParams: Record<string, string>,
): Array<LogicWithTypeAndQuestionId> => {
    return logics
        .filter(isExtendedLogic)
        .map((logic) => {
            const conditionsWithMatchedFlag: Array<{
                cond: LogicSubCondition;
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
                qId: number;
            }> = logic.conditions.map((cond) => {
                if (cond.type === LogicSubConditonType.LINK_PARAM) {
                    return {
                        cond,
                        isMatched:
                            cond.respondent === LogicType.SELECT_VARIANTS
                                ? linkParams[cond.linkParamId]
                                      ?.toLowerCase()
                                      ?.includes(cond.linkParamValue?.toLowerCase())
                                : !linkParams[cond.linkParamId]
                                      ?.toLowerCase()
                                      ?.includes(cond.linkParamValue?.toLowerCase()),
                        boolFuncExternal: cond.boolFuncExternal,
                        qId: -1,
                    };
                }
                let question: QuestionActorRef | null = null;

                bundles.forEach((bundle) =>
                    bundle.questions.forEach((questionActor) => {
                        if (questionActor.getSnapshot().context.id === cond.questionId) {
                            question = questionActor;
                        }
                    }),
                );

                if (question) {
                    const isMatched = logicMatchCommonOrExtended(
                        {
                            id: cond.id,
                            boolFunc: cond.boolFunc,
                            type: cond.respondent,
                            params: cond.variants
                                .filter((v) => v.checked)
                                .map((v) => ({ id: v.id as number, checked: v.checked })),
                            minCount: cond.minCount,
                            maxCount: cond.maxCount,
                        },
                        question,
                    );

                    return {
                        cond,
                        isMatched,
                        boolFuncExternal: cond.boolFuncExternal,
                        qId: question.getSnapshot().context.id,
                    };
                }
                return {
                    cond,
                    isMatched: false,
                    boolFuncExternal: cond.boolFuncExternal,
                    qId: -1,
                };
            });
            const matchedConditions = conditionsWithMatchedFlag
                .filter((cond) => cond.isMatched)
                .map(({ cond }) => cond);
            const conditionsWithMatchedFlagWithCollapsedAnd: Array<{
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
                qId: number;
            }> = [];
            let isOpenBracket = false;
            let addConds: Array<{
                isMatched: boolean;
                boolFuncExternal: LogicBoolFunc;
                qId: number;
            }> = [];
            conditionsWithMatchedFlag.forEach((cond) => {
                if (cond.boolFuncExternal === LogicBoolFunc.AND) {
                    isOpenBracket = true;
                    addConds.push(cond);
                } else {
                    if (isOpenBracket) {
                        addConds.push(cond);
                        conditionsWithMatchedFlagWithCollapsedAnd.push({
                            boolFuncExternal: cond.boolFuncExternal,
                            isMatched: addConds.every((c) => c.isMatched),
                            qId: getQIdWithMaxIndex(
                                addConds.map((cond) => cond.qId),
                                bundles,
                            ),
                        });
                        isOpenBracket = false;
                        addConds = [];
                    } else {
                        conditionsWithMatchedFlagWithCollapsedAnd.push({
                            isMatched: cond.isMatched,
                            boolFuncExternal: cond.boolFuncExternal,
                            qId: cond.qId,
                        });
                    }
                }
            });
            if (isOpenBracket) {
                conditionsWithMatchedFlagWithCollapsedAnd.push({
                    boolFuncExternal: LogicBoolFunc.OR,
                    isMatched: addConds.every((c) => c.isMatched),
                    qId: getQIdWithMaxIndex(
                        addConds.map((cond) => cond.qId),
                        bundles,
                    ),
                });
                isOpenBracket = false;
                addConds = [];
            }
            return {
                isMatched: conditionsWithMatchedFlagWithCollapsedAnd.some((c) => c.isMatched),
                logic: { ...logic, conditions: matchedConditions },
                qId: getQIdWithMaxIndex(
                    conditionsWithMatchedFlagWithCollapsedAnd.map((cond) => cond.qId),
                    bundles,
                ),
            };
        })
        .filter((logic) => logic.isMatched)
        .map(({ logic, qId }) => ({ ...logic, qId, logicType: 'extended' }));
};

export const getMatchedCommonLogics = (bundles: Array<BundleEntity>): Array<LogicWithTypeAndQuestionId> => {
    const matchedCommonLogics: Array<LogicWithTypeAndQuestionId> = [];
    bundles.forEach((bundle) => {
        bundle.questions.forEach((question) => {
            question
                .getSnapshot()
                .context.logics.filter(isCommonLogic)
                .forEach((logic) => {
                    if (logicMatchCommonOrExtended(logic, question)) {
                        matchedCommonLogics.push({
                            ...logic,
                            qId: question.getSnapshot().context.id,
                            logicType: 'common',
                        });
                    }
                });
        });
    });

    return matchedCommonLogics;
};

const getQuestionsBetweenTwoIds = (bundles: Array<BundleEntity>, startId: number, endId: number): Array<number> => {
    const qIds: Array<number> = [];
    const startBundleIdx = bundles.findIndex((bundle) =>
        bundle.questions.find((q) => q.getSnapshot().context.id === startId),
    );
    const startQuestionIdx =
        bundles[startBundleIdx]?.questions.findIndex((q) => q.getSnapshot().context.id === startId) ?? -1;
    const endBundleIdx = bundles.findIndex((bundle) =>
        bundle.questions.find((q) => q.getSnapshot().context.id === endId),
    );
    const endQuestionIdx =
        bundles[endBundleIdx]?.questions.findIndex((q) => q.getSnapshot().context.id === endId) ?? -1;
    if (startQuestionIdx === -1 || endQuestionIdx === -1) return [];
    bundles.forEach((bundle, bundleIdx) => {
        if (bundleIdx < startBundleIdx || bundleIdx > endBundleIdx) return;
        bundle.questions.forEach((question, questionIdx) => {
            if (bundleIdx === startBundleIdx && questionIdx <= startQuestionIdx) {
                return;
            }
            if (bundleIdx === endBundleIdx && questionIdx >= endQuestionIdx) {
                return;
            }
            qIds.push(question.getSnapshot().context.id);
        });
    });

    return qIds;
};

const getQuestionsAfterId = (bundles: Array<BundleEntity>, startId: number): Array<number> => {
    const qIds: Array<number> = [];
    const startBundleIdx = bundles.findIndex((bundle) =>
        bundle.questions.find((q) => q.getSnapshot().context.id === startId),
    );
    const startQuestionIdx =
        bundles[startBundleIdx]?.questions.findIndex((q) => q.getSnapshot().context.id === startId) ?? -1;

    bundles.forEach((bundle, bundleIdx) => {
        if (bundleIdx < startBundleIdx) return;
        bundle.questions.forEach((question, questionIdx) => {
            if (bundleIdx === startBundleIdx && questionIdx <= startQuestionIdx) return [];
            qIds.push(question.getSnapshot().context.id);
        });
    });
    return qIds;
};

const getAllQuestionIdsForBundles = (bundles: Array<BundleEntity>, pageIds: Array<number>): Array<number> => {
    const qIds: Array<number> = [];
    bundles.forEach((bundle) => {
        if (pageIds.includes(bundle.page.id)) {
            qIds.push(...bundle.questions.map((q) => q.getSnapshot().context.id));
        }
    });
    return qIds;
};
type InternalLogicType =
    | 'to-question'
    | 'to-page'
    | 'show-questions'
    | 'show-pages'
    | 'hide-questions'
    | 'hide-pages'
    | 'finish';
const getHiddenQuestionIdsByType = (
    bundles: Array<BundleEntity>,
    logics: Array<LogicWithTypeAndQuestionId>,
    type: InternalLogicType,
) => {
    const hiddenQuestionIds: Set<number> = new Set();
    if (logics.length === 0) return [];

    switch (type) {
        case 'to-question': {
            logics.forEach((logic) => {
                const startQuestion = logic.qId;
                const endQuestion = logic.toQuestion;
                getQuestionsBetweenTwoIds(bundles, startQuestion, endQuestion).forEach((qId) =>
                    hiddenQuestionIds.add(qId),
                );
            });
            break;
        }
        case 'to-page': {
            logics.forEach((logic) => {
                const startQuestion = logic.qId;
                const toBundle = bundles.find((bundle) => bundle.page.id === logic.toPage);
                const endQuestion = toBundle?.questions?.[0]?.getSnapshot().context.id ?? -1;
                getQuestionsBetweenTwoIds(bundles, startQuestion, endQuestion).forEach((qId) =>
                    hiddenQuestionIds.add(qId),
                );
            });
            break;
        }
        case 'show-questions': {
            const allToQuestions = logics.flatMap((l) => l.toQuestions ?? []);
            const hidden = new Set<number>();
            logics.forEach((logic) => {
                const startQuestion = logic.qId;
                getQuestionsAfterId(bundles, startQuestion).forEach((qId) => hidden.add(qId));
            });
            allToQuestions.forEach((toQuestion) => hidden.delete(toQuestion));
            [...hidden.keys()].forEach((key) => hiddenQuestionIds.add(key));
            break;
        }
        case 'show-pages': {
            const isSomeOfLogicsHasQuestionTriggered = logics.some((l) => l.qId !== -1);
            const allToPages = logics
                .flatMap((l) => l.toPages ?? [])
                .filter((page) => !isSomeOfLogicsHasQuestionTriggered || page !== bundles?.[0]?.page?.id);

            const hidden = new Set<number>();
            logics.forEach((logic) => {
                let startQuestion = logic.qId;
                if (startQuestion === -1 && logic.toPages.includes(bundles?.[0]?.page?.id)) {
                    startQuestion = bundles?.[1]?.questions?.[0]?.getSnapshot().context.id ?? -1;
                }
                getQuestionsAfterId(bundles, startQuestion).forEach((qId) => hidden.add(qId));
            });
            getAllQuestionIdsForBundles(bundles, allToPages).forEach((toQuestion) => hidden.delete(toQuestion));
            [...hidden.keys()].forEach((key) => hiddenQuestionIds.add(key));
            break;
        }
        case 'hide-questions': {
            const allToQuestions = logics.flatMap((l) => l.toQuestions ?? []);
            allToQuestions.forEach((toQuestion) => hiddenQuestionIds.add(toQuestion));
            break;
        }
        case 'hide-pages': {
            const allToPages = logics.flatMap((l) => l.toPages ?? []);
            getAllQuestionIdsForBundles(bundles, allToPages).forEach((toQuestion) => hiddenQuestionIds.add(toQuestion));
            break;
        }
        case 'finish': {
            logics.forEach((logic) => {
                const startQuestion = logic.qId;
                getQuestionsAfterId(
                    bundles,
                    logic.finishTransitionVariant === LogicTransitionVariant.AFTER_QUESTION
                        ? logic.afterQuestionId
                        : startQuestion,
                ).forEach((qId) => hiddenQuestionIds.add(qId));
            });
            break;
        }
    }
    return Array.from(hiddenQuestionIds);
};
type LogicWithTypeAndQuestionId = QuestionLogicModel & {
    logicType: 'quota' | 'link-param' | 'extended' | 'common';
    qId: number;
};

export const getHiddenAnswers = (logics: Array<LogicWithTypeAndQuestionId>): Record<number, Array<number>> => {
    const hiddenAnswers: Record<number, Array<number>> = {};
    logics.filter(isAnswersHideActionLogic).forEach((logic) => {
        if (!hiddenAnswers[logic.toQuestion]) {
            hiddenAnswers[logic.toQuestion] = [];
        }
        if (Array.isArray(logic.answersToHide)) {
            hiddenAnswers[logic.toQuestion] = [...hiddenAnswers[logic.toQuestion], ...logic.answersToHide];
        }
    });
    return hiddenAnswers;
};

const getIsAllQuestionsHideByLinkParamLogics = (
    bundles: Array<BundleEntity>,
    extendedLogic: Array<LogicWithTypeAndQuestionId>,
    linkParamsLogic: Array<LogicWithTypeAndQuestionId>,
) => {
    const extendedLogicWithLinkParam = extendedLogic.filter((logic) =>
        logic.conditions.some((cond) => cond.type === LogicSubConditonType.LINK_PARAM),
    );
    const newBundles = getFilteredBundles(bundles, [...linkParamsLogic, ...extendedLogicWithLinkParam]);
    return newBundles.length === 0;
};

const filterOnlyLogicsWithQId = (logics: Array<LogicWithTypeAndQuestionId>, qId: number) =>
    logics.filter((l) => l.qId === qId);
const getFilteredBundles = (bundles: Array<BundleEntity>, logicsRaw: Array<LogicWithTypeAndQuestionId>) => {
    const logics = logicsRaw.filter(isNotAnswersHideActionLogic);
    const toQuestionLogics = logics.filter((logic) => logic.transition === LogicTransition.QUESTION);
    const toPageLogics = logics.filter((logic) => logic.transition === LogicTransition.PAGE);
    const showQuestionsLogics = logics.filter(
        (logic) => logic.transition === LogicTransition.QUESTIONS && logic.action !== LogicAction.HIDE,
    );
    const showPagesLogics = logics.filter(
        (logic) => logic.transition === LogicTransition.PAGES && logic.action !== LogicAction.HIDE,
    );
    const hideQuestionsLogics = logics.filter(
        (logic) => logic.transition === LogicTransition.QUESTIONS && logic.action === LogicAction.HIDE,
    );
    const hidePagesLogics = logics.filter(
        (logic) => logic.transition === LogicTransition.PAGES && logic.action === LogicAction.HIDE,
    );
    const finishTransitions = logics.filter(isLogicWithOnlyFinishType);
    const acumShowQuestions = filterOnlyLogicsWithQId(showQuestionsLogics, -1);
    const acumShowPages = filterOnlyLogicsWithQId(showPagesLogics, -1);
    const hiddenQuestionIds = getHiddenQuestionIdsByType(
        bundles,
        filterOnlyLogicsWithQId(toQuestionLogics, -1),
        'to-question',
    )
        .concat(getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(toPageLogics, -1), 'to-page'))

        .concat(getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(hideQuestionsLogics, -1), 'hide-questions'))
        .concat(getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(hidePagesLogics, -1), 'hide-pages'))
        .concat(getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(finishTransitions, -1), 'finish'));
    const nextBundles: Array<BundleEntity> = [];
    let hiddenQuestionIdsByPagesAndQuestions = getHiddenQuestionIdsByType(
        bundles,
        acumShowQuestions,
        'show-questions',
    ).concat(getHiddenQuestionIdsByType(bundles, acumShowPages, 'show-pages'));
    for (const bundle of bundles) {
        const nextQuestions = [];
        for (const question of bundle.questions) {
            const qId = question.getSnapshot().context.id;
            if (hiddenQuestionIds.includes(qId) || hiddenQuestionIdsByPagesAndQuestions.includes(qId)) {
                continue;
            }
            acumShowQuestions.push(...filterOnlyLogicsWithQId(showQuestionsLogics, qId));
            acumShowPages.push(...filterOnlyLogicsWithQId(showPagesLogics, qId));
            hiddenQuestionIdsByPagesAndQuestions = getHiddenQuestionIdsByType(
                bundles,
                acumShowQuestions,
                'show-questions',
            ).concat(getHiddenQuestionIdsByType(bundles, acumShowPages, 'show-pages'));
            hiddenQuestionIds.push(
                ...getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(toQuestionLogics, qId), 'to-question')
                    .concat(getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(toPageLogics, qId), 'to-page'))
                    .concat(
                        getHiddenQuestionIdsByType(
                            bundles,
                            filterOnlyLogicsWithQId(hideQuestionsLogics, qId),
                            'hide-questions',
                        ),
                    )
                    .concat(
                        getHiddenQuestionIdsByType(
                            bundles,
                            filterOnlyLogicsWithQId(hidePagesLogics, qId),
                            'hide-pages',
                        ),
                    )
                    .concat(
                        getHiddenQuestionIdsByType(bundles, filterOnlyLogicsWithQId(finishTransitions, qId), 'finish'),
                    ),
            );
            nextQuestions.push(question);
        }
        if (nextQuestions.length > 0) {
            nextBundles.push(
                new BundleEntity(
                    {
                        ...bundle,
                    },
                    nextQuestions,
                ),
            );
        }
    }
    // const hiddenQuestionIds = getHiddenQuestionIdsByType(bundles, toQuestionLogics, 'to-question')
    //     .concat(getHiddenQuestionIdsByType(bundles, toPageLogics, 'to-page'))
    //     .concat(getHiddenQuestionIdsByType(bundles, showQuestionsLogics, 'show-questions'))
    //     .concat(getHiddenQuestionIdsByType(bundles, showPagesLogics, 'show-pages'))
    //     .concat(getHiddenQuestionIdsByType(bundles, hideQuestionsLogics, 'hide-questions'))
    //     .concat(getHiddenQuestionIdsByType(bundles, hidePagesLogics, 'hide-pages'))
    //     .concat(getHiddenQuestionIdsByType(bundles, finishTransitions, 'finish'));
    // const newBundles = bundles.reduce((acc, bundle) => {
    //     const nextBundle: BundleEntity = new BundleEntity(
    //         {
    //             ...bundle,
    //         },
    //         bundle.questions.filter((q) => !hiddenQuestionIds.includes(q.getSnapshot().context.id)),
    //     );
    //     if (nextBundle.questions.length > 0) {
    //         acc.push(nextBundle);
    //     }
    //     return acc;
    // }, [] as Array<BundleEntity>);
    return nextBundles;
};

const excludeQuestionsWithFullHiddenAnswers = (
    bundles: Array<BundleEntity>,
    hiddenAnswers: Record<number, Array<number>>,
) => {
    const nextBundles = bundles.map((bundle) => {
        return new BundleEntity(
            bundle,
            bundle.questions.filter((q) => {
                if (
                    Array.isArray(hiddenAnswers[q.getSnapshot().context.id]) &&
                    hiddenAnswers[q.getSnapshot().context.id].length > 0 &&
                    q
                        .getSnapshot()
                        .context.answersActors.every((actor) =>
                            hiddenAnswers[q.getSnapshot().context.id].includes(actor.getSnapshot().context.id),
                        )
                ) {
                    return false;
                }
                return true;
            }),
        );
    });
    return nextBundles.filter((bundle) => bundle.type !== BundleType.QUESTIONS || bundle.questions.length > 0);
};

export const adjustQuestions = (startBundles: Array<BundleEntity>, finishBundles: Array<BundleEntity>): void => {
    const finishBundlesQuestionIds = finishBundles.flatMap((b) => b.questions.map((q) => q.getSnapshot().context.id));
    let questionOrder = 0;
    finishBundles.forEach(({ questions }) => {
        questions.forEach((q) => {
            if (isOrderVisible(q.getSnapshot().context.type)) {
                q.send({ type: 'SET_ORDER', value: ++questionOrder });
            }
        });
    });
    startBundles.forEach((b) =>
        b.questions.forEach((q) => {
            q.getSnapshot().context.showByLogic = true;
            q.send({ type: 'SET_SHOW_BY_LOGIC', value: finishBundlesQuestionIds.includes(q.getSnapshot().context.id) });
        }),
    );
};
export const filterBundlesQuestionsByLogicNew = (
    bundles: Array<BundleEntity>,
    globalLogic: Array<QuestionLogicModel>,
    queryParamsRaw: Record<string, string>,
    linkParamsRaw: Record<string, string>,
    extendedLogic: Array<QuestionLogicModel>,
): { newBundles: Array<BundleEntity>; hiddenAnswers: Record<number, Array<number>> } => {
    const matchedLinkParams = getMatchedLinkParams(queryParamsRaw, linkParamsRaw);
    const root = bundles[0]?.questions[0]?._parent.getSnapshot().context as SurveyStore;
    const quotaByQuestionId = root?.quotaRef?.getSnapshot().context.quotaStatusByQuestionId || {};

    let matchedLinkParamsLogics = getMatchedLinkParamsLogics(globalLogic.filter(isLinkParamLogic), matchedLinkParams);
    const matchedQuotaLogics = getMatchedQuotaLogics(bundles, quotaByQuestionId);
    let matchedExtendedLogics = getMatchedExtendedLogics(bundles, extendedLogic, matchedLinkParams);
    const matchedCommonLogics = getMatchedCommonLogics(bundles);
    const isAllQuestionsHideByLinkParamLogics = getIsAllQuestionsHideByLinkParamLogics(
        bundles,
        matchedExtendedLogics,
        matchedLinkParamsLogics,
    );

    if (isAllQuestionsHideByLinkParamLogics) {
        matchedLinkParamsLogics = [];
        matchedExtendedLogics = matchedExtendedLogics.filter(
            (l) => !l.conditions.some((cond) => cond.type === LogicSubConditonType.LINK_PARAM),
        );
    }

    // let newBundles: Array<BundleEntity> = [];
    // let allMatchedLogics: Array<LogicWithTypeAndQuestionId> = [];
    // let bundleIdx = 0,
    //     questionIdx = 0;
    // let tmpBundles = bundles[0] ? [new BundleEntity(bundles[0], bundles[0].questions.slice(0, 1))] : [];
    // while (bundleIdx < bundles.length) {
    //     const matchedExtendedLogics = isAllQuestionsHideByLinkParamLogics
    //         ? getMatchedExtendedLogics(tmpBundles, extendedLogic, matchedLinkParams).filter(
    //               (l) => !l.conditions.some((cond) => cond.type === LogicSubConditonType.LINK_PARAM),
    //           )
    //         : getMatchedExtendedLogics(tmpBundles, extendedLogic, matchedLinkParams);
    //     const matchedCommonLogics = getMatchedCommonLogics(tmpBundles);
    //     allMatchedLogics = matchedLinkParamsLogics
    //         .concat(matchedCommonLogics)
    //         .concat(matchedExtendedLogics)
    //         .concat(matchedQuotaLogics);

    //     newBundles = getFilteredBundles(tmpBundles, allMatchedLogics);
    //     adjustQuestions(bundles, newBundles);
    //     const curBundle = bundles[bundleIdx];
    //     const curBundleMaxQuestionIdx = curBundle ? curBundle.questions.length - 1 : -1;
    //     if (questionIdx >= curBundleMaxQuestionIdx) {
    //         questionIdx = 0;
    //         bundleIdx += 1;
    //     } else {
    //         questionIdx += 1;
    //     }
    //     tmpBundles = bundles.slice(0, bundleIdx + 1);
    //     if (tmpBundles[tmpBundles.length - 1]) {
    //         tmpBundles[tmpBundles.length - 1] = new BundleEntity(
    //             tmpBundles[tmpBundles.length - 1],
    //             tmpBundles[tmpBundles.length - 1].questions.slice(0, questionIdx + 1),
    //         );
    //     }
    // }
    const allMatchedLogics = matchedLinkParamsLogics
        .concat(matchedCommonLogics)
        .concat(matchedExtendedLogics)
        .concat(matchedQuotaLogics);
    const newBundles = getFilteredBundles(bundles, allMatchedLogics);
    const lastMatchedLogicsWithFinishType = filterLogicsByFinishTypeOrder(allMatchedLogics, bundles)?.[0];
    if (
        lastMatchedLogicsWithFinishType &&
        lastMatchedLogicsWithFinishType.transitionType !== LogicTransitionType.STANDART
    ) {
        newBundles.push(createLastBundle(lastMatchedLogicsWithFinishType));
    }
    const hiddenAnswers = getHiddenAnswers(allMatchedLogics);
    const nextBundles = excludeQuestionsWithFullHiddenAnswers(newBundles, hiddenAnswers);
    adjustQuestions(bundles, nextBundles);
    return {
        newBundles: nextBundles,
        hiddenAnswers,
    };
};
